/**
 * Black Tulip v3 — cart drawer and pre-checkout page.
 *
 * Quantity, removal and the discount field, against Shopify's /cart AJAX API.
 * The drawer and the page share this file because they share their markup;
 * a line must not behave differently depending on which surface it is on.
 */
(function () {
  'use strict';

  // ONCE PER PAGE — 26 September. The cart page and the cart drawer both emit
  // this script, and the drawer is on every page, so /cart ran it twice: two
  // click listeners and two change listeners, so every quantity step or removal
  // sent TWO POST /cart/change.js and two re-renders, and every note save two
  // POST /cart/update.js. Every listener here is delegated on document, so the
  // first run already covers anything either section renders, now or later.
  // The same guard v3-named-date.js carries, for the same reason.
  if (window.BTCart) return;

  var busy = false;

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      // keepalive, so the browser finishes the request even if the page is on
      // its way out. Checkout is a plain anchor: pressing it blurs the note
      // field, which fires `change`, which starts this fetch — and the
      // navigation begins in the same breath. Without this the browser is free
      // to abort it, so whether the last thing she typed was saved was a race
      // she could not see and could not win. It covers closing the tab too.
      keepalive: true,
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (e) { throw e; }, function () { throw new Error(r.status); });
      return r.json();
    });
  }

  /** Re-render from the server rather than patching the DOM: the totals, the
   *  threshold line and the per-size recap all derive from the whole cart, and
   *  patching one line silently leaves the other three stale. */
  function refresh() {
    return fetch(window.location.pathname + '?section_id=' + currentSectionId(), {
      headers: { 'Accept': 'text/html' }
    }).then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (html) { swap(html); return; }
        window.location.reload();
      })
      .catch(function () { window.location.reload(); });
  }

  function currentSectionId() {
    var root = document.querySelector('[data-v3-cart-drawer],[data-v3-cart-form]');
    var sec = root && root.closest('[data-section-id]');
    return sec ? sec.dataset.sectionId : '';
  }

  function swap(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var pairs = [
      ['[data-v3-cd-body]', '[data-v3-cd-body]'],
      ['.v3-cd__foot', '.v3-cd__foot'],
      ['.v3-cart-grid', '.v3-cart-grid'],
    ];
    // What she has typed outlives the re-render. The note lives inside
    // .v3-cart-grid, which is replaced wholesale on every quantity change and
    // removal — and the replacement is server-rendered {{ cart.note }} from an
    // independent request, so the two are unordered. Adjust a quantity just
    // after typing and her words could come back blank from a response that
    // was prepared before they were saved. The DOM is not the record here; the
    // field she is looking at is.
    var noteEl = document.querySelector('[data-v3-note]');
    var noteVal = noteEl ? noteEl.value : null;
    var noteHadFocus = noteEl ? document.activeElement === noteEl : false;
    var noteStart = noteHadFocus ? noteEl.selectionStart : null;
    var noteEnd = noteHadFocus ? noteEl.selectionEnd : null;

    // WHEN THE BAG EMPTIES ON THE CART PAGE the response has no form and no
    // grid at all — the page is a different shape. Swapping the grid alone
    // found nothing to copy from and left the last line and its subtotal on
    // screen. So when the grid is on one side and not the other, the whole
    // page container is swapped; otherwise only the parts that change, as
    // before, so focus and the note field are disturbed as little as possible.
    var pageTo = document.querySelector('[data-v3-cart-page]');
    var pageFrom = doc.querySelector('[data-v3-cart-page]');
    var reshaped = pageTo && pageFrom &&
      !!document.querySelector('.v3-cart-grid') !== !!doc.querySelector('.v3-cart-grid');
    if (reshaped) {
      pageTo.className = pageFrom.className;
      pageTo.innerHTML = pageFrom.innerHTML;
    }

    for (var i = 0; i < pairs.length; i++) {
      var to = document.querySelector(pairs[i][0]);
      var from = doc.querySelector(pairs[i][1]);
      // `hidden` travels with the content: the drawer footer is always in the
      // document and hidden while the bag is empty (see v3-cart-drawer.liquid).
      if (to && from) { to.innerHTML = from.innerHTML; to.hidden = from.hidden; }
    }

    if (noteVal !== null) {
      var fresh = document.querySelector('[data-v3-note]');
      if (fresh) {
        fresh.value = noteVal;
        if (noteHadFocus) {
          fresh.focus();
          try { fresh.setSelectionRange(noteStart, noteEnd); } catch (e) { /* not selectable */ }
        }
      }
    }
    // FILL THE DATES, DON'T ONLY HIDE THEM — 26 September. The swapped-in HTML
    // carries the date span EMPTY, because the server cannot know when the page
    // is read. This loop used to hide the spans with no date and do nothing for
    // the rest, so the drawer's "Arrives by" went blank after every change —
    // every quick-add, every quantity step. render() writes the date or hides
    // the span, whichever is true, from the approved string.
    if (window.BTDate && window.BTDate.renderAll) window.BTDate.renderAll();
    countBag();
    document.dispatchEvent(new CustomEvent('v3:cart:updated'));
  }

  /* THE HEADER COUNT — W-20261019-3, 19 Oct 2026. "Bag (n)" lives in the
     header, which no re-render touches (the swap replaces the drawer and the
     cart grid only), so after a quick add or a removal it kept the old number
     until the next page load. One read of /cart.js after each swap sets every
     [data-v3-cart-count] to the bag's real item count, in the header's own
     "(n)" form. A failed read leaves the number as it was. */
  function countBag() {
    var els = document.querySelectorAll('[data-v3-cart-count]');
    if (!els.length || !window.fetch) return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        if (!c || typeof c.item_count !== 'number') return;
        for (var i = 0; i < els.length; i++) els[i].textContent = '(' + c.item_count + ')';
      })
      .catch(function () { /* the number stays as it was */ });
  }

  function change(key, quantity) {
    if (busy) return;
    busy = true;
    post('/cart/change.js', { id: key, quantity: quantity })
      .then(refresh)
      .catch(function () { window.location.reload(); })
      .then(function () { busy = false; }, function () { busy = false; });
  }

  document.addEventListener('click', function (e) {
    var step = e.target.closest('[data-v3-qty]');
    if (step) {
      var line = step.closest('[data-v3-line]');
      var input = line.querySelector('[data-v3-qty-input]');
      var n = parseInt(input.value, 10) || 0;
      n = step.dataset.v3Qty === '+' ? n + 1 : n - 1;
      if (n < 0) n = 0;
      change(line.dataset.key, n);
      return;
    }

    var rm = e.target.closest('[data-v3-remove]');
    if (rm) { change(rm.closest('[data-v3-line]').dataset.key, 0); return; }

    var apply = e.target.closest('[data-v3-discount-apply]');
    if (apply) { applyDiscount(apply); return; }

    var gift = e.target.closest('[data-v3-gift-add]');
    if (gift) { addGift(gift); return; }

    var close = e.target.closest('[data-v3-cd-close],[data-v3-cd-scrim]');
    if (close) { closeDrawer(); return; }

    var open = e.target.closest('[data-v3-cart-open]');
    if (open) { e.preventDefault(); openDrawer(); }
  });

  document.addEventListener('change', function (e) {
    if (!e.target.matches('[data-v3-qty-input]')) return;
    var n = parseInt(e.target.value, 10);
    if (!isFinite(n) || n < 0) n = 0;
    change(e.target.closest('[data-v3-line]').dataset.key, n);
  });

  /**
   * Shopify has no storefront endpoint that validates a discount code, so this
   * field cannot judge one. It can still put the answer where she asked for it.
   *
   * It used to redirect to /checkout, and Yash's review on 15 September is the
   * reason that is wrong: typing a code to SEE what it does took her out of the
   * bag and into checkout whether the code worked or not. Asking a question
   * should not commit you to buying.
   *
   * /discount/<code> applies the code to the session and honours ?redirect, so
   * the code lands and she stays in the bag, where the discount line and the new
   * total are already rendered. She goes to checkout when she means to.
   *
   * The cost of staying is that an invalid code does nothing visible — checkout
   * would at least have said so. So a flag is set before navigating and read
   * back after: no discount on the page and the flag still set means the code
   * did not take, and the error that already existed for an empty submission
   * says so. sessionStorage rather than a query parameter, because Shopify's
   * redirect is not documented to preserve one and a lost flag would report a
   * good code as bad. If storage throws — a private window — the flag is simply
   * never set, and the page behaves as it did before: silent.
   */
  var TRIED = 'v3-discount-tried';

  function applyDiscount(btn) {
    var wrap = btn.closest('.v3-disc');
    var input = wrap.querySelector('[data-v3-discount]');
    var err = wrap.parentNode.querySelector('[data-v3-discount-error]');
    var code = (input.value || '').trim();
    if (!code) { if (err) err.hidden = false; input.focus(); return; }
    if (err) err.hidden = true;
    try { sessionStorage.setItem(TRIED, code); } catch (e) { /* private window */ }
    window.location.href = '/discount/' + encodeURIComponent(code) +
      '?redirect=' + encodeURIComponent('/cart');
  }

  /**
   * Did the code we just sent actually take? A cart-level discount renders
   * .v3-totals--disc; a line-level one strikes the original through as
   * .v3-line__was. Either means something applied. The flag is cleared whatever
   * the answer, so the message never survives into an unrelated page view.
   */
  function reportDiscount() {
    var tried = null;
    try { tried = sessionStorage.getItem(TRIED); } catch (e) { return; }
    if (!tried) return;
    try { sessionStorage.removeItem(TRIED); } catch (e) { /* nothing to do */ }
    if (document.querySelector('.v3-totals--disc, .v3-line__was')) return;
    var errs = document.querySelectorAll('[data-v3-discount-error]');
    for (var i = 0; i < errs.length; i++) errs[i].hidden = false;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reportDiscount);
  } else {
    reportDiscount();
  }

  /**
   * THE FREE GIFT — 1 October 2026. snippets/v3-cart-gift.liquid has the
   * rulings; this is the behaviour.
   *
   * Adding: the chosen tile's variant goes in with the hidden line property
   * `_gift`, which is what marks it as the gift in the bag. The theme gives
   * nothing away itself: the automatic discount makes the line £0.
   *
   * Taking out: v3-cart-lines.liquid marks a gift line data-v3-gift-drop when
   * the rest of the bag has fallen under its threshold, or when the line is
   * not £0 although the bag is over it (a discount code that does not combine
   * with the gift). Left alone, she would pay full price for something she
   * took as free. So it comes out, and the sentence the server put on the line
   * is shown under the bag once the re-render is done, because the re-render
   * replaces the line it was read from.
   *
   * Each line is tried once per page view: a removal that keeps failing must
   * not become a loop of requests. The same check runs on load, because a
   * discount code is applied by a full page load (/discount/<code>), and after
   * every re-render.
   */
  var giftTried = {};
  var giftBusy = false;

  function addGift(btn) {
    if (busy || giftBusy) return;
    var panel = btn.closest('[data-v3-gift]');
    var pick = panel && panel.querySelector('input[type="radio"]:checked');
    if (!pick) return;
    busy = true;
    btn.disabled = true;
    post('/cart/add.js', {
      items: [{ id: parseInt(pick.value, 10), quantity: 1, properties: { _gift: pick.dataset.kind } }]
    })
      .then(refresh)
      .catch(function () { window.location.reload(); })
      .then(function () { busy = false; }, function () { busy = false; });
  }

  function giftNote(msg) {
    var notes = document.querySelectorAll('[data-v3-gift-note]');
    for (var i = 0; i < notes.length; i++) {
      notes[i].textContent = msg;
      notes[i].hidden = !msg;
    }
  }

  function checkGift() {
    if (giftBusy) return;
    var line = document.querySelector('[data-v3-gift-drop]');
    if (!line || giftTried[line.dataset.key]) return;
    giftTried[line.dataset.key] = true;
    giftBusy = true;
    var msg = line.dataset.v3GiftMsg || '';
    post('/cart/change.js', { id: line.dataset.key, quantity: 0 })
      .then(refresh)
      .then(function () { giftNote(msg); })
      .catch(function () { /* left as it is; the line still shows its real price */ })
      .then(function () { giftBusy = false; }, function () { giftBusy = false; });
  }

  document.addEventListener('v3:cart:updated', checkGift);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkGift);
  } else {
    checkGift();
  }

  var lastFocus = null;
  function openDrawer() {
    var d = document.querySelector('[data-v3-cart-drawer]');
    var s = document.querySelector('[data-v3-cd-scrim]');
    if (!d) return;
    lastFocus = document.activeElement;
    d.setAttribute('data-open', '');
    d.removeAttribute('aria-hidden');
    if (s) s.setAttribute('data-open', '');
    var close = d.querySelector('[data-v3-cd-close]');
    if (close) close.focus();
  }
  function closeDrawer() {
    var d = document.querySelector('[data-v3-cart-drawer]');
    var s = document.querySelector('[data-v3-cd-scrim]');
    if (!d) return;
    d.removeAttribute('data-open');
    d.setAttribute('aria-hidden', 'true');
    if (s) s.removeAttribute('data-open');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  document.addEventListener('keydown', function (e) {
    var d = document.querySelector('[data-v3-cart-drawer][data-open]');
    if (e.key === 'Escape' && d) closeDrawer();
  });

  /**
   * The note, saved as she leaves the field, and now SAID.
   *
   * The failure path used to be `.catch(function () {})` — a 422, a dropped
   * connection, anything, swallowed whole, with the field still sitting there
   * looking as though it had been kept. The discount field beside it has both
   * an error element and a check that the code really applied; this had
   * neither.
   *
   * It retries once, because the common failure is a flaky connection rather
   * than a bad request, and then marks the field. The SUCCESS path was silent
   * for the same reason the failure path was — there was no approved string —
   * and that is the half people actually met: she types, the field blurs, the
   * page does not move, and a saved note looks exactly like a lost one.
   * v3.cart.note_saved landed on 17 September and the confirmation is here.
   *
   * There is still no string for a FAILED save, so that path is unchanged:
   * aria-invalid and the marker, no invented sentence.
   *
   * The string is read off the element rather than held here, because this file
   * is an asset and assets cannot call `t`. The same reason the finder drawer
   * carries data-strings.
   */
  function noteState(el) {
    var id = el.getAttribute('aria-describedby');
    return id ? document.getElementById(id) : null;
  }

  function saveNote(el, retry) {
    return post('/cart/update.js', { note: el.value })
      .then(function () {
        el.removeAttribute('aria-invalid');
        el.removeAttribute('data-v3-note-failed');
        var s = noteState(el);
        if (s) s.textContent = s.dataset.saved || '';
      })
      .catch(function () {
        if (!retry) return saveNote(el, true);
        el.setAttribute('aria-invalid', 'true');
        el.setAttribute('data-v3-note-failed', '');
        var s = noteState(el);
        if (s) s.textContent = '';
      });
  }

  document.addEventListener('change', function (e) {
    if (!e.target.matches('[data-v3-note]')) return;
    saveNote(e.target, false);
  });

  // The confirmation is about the text that was saved, so the next keystroke
  // retires it. Without this it sits under a field she has since edited and
  // says the edit is safe, which is the failure this was built to fix, only
  // harder to catch.
  document.addEventListener('input', function (e) {
    if (!e.target.matches('[data-v3-note]')) return;
    var s = noteState(e.target);
    if (s && s.textContent) s.textContent = '';
  });

  window.BTCart = { open: openDrawer, close: closeDrawer, refresh: refresh };
})();
